import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_MY_CARRIER_PORTAL_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

const formSchema = z.object({
  apiPassword: z.string().min(1, 'API Password is required'),
  onboardingURL: z.string().min(1, 'Onboarding URL is required').url('Must be a valid URL'),
  username: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the MyCarrierPortal app.
 */
export function MyCarrierPortalModal({ isOpen, onClose, appType }: AppModalProps) {
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      apiPassword: '',
      onboardingURL: '',
      username: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableMyCarrierPortalApp, { loading }] = useMutation(ENABLE_MY_CARRIER_PORTAL_APP, {
    onCompleted: () => {
      toast.success('MyCarrierPortal app is enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage = `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableMyCarrierPortalApp({
      variables: {
        appType: appType as AppTypeEnum,
        onboardingURL: values.onboardingURL,
        password: values.apiPassword,
        username: values.username
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable MyCarrierPortal for Chain</DialogTitle>
          <DialogDescription>
            MyCarrierPortal integration requires an API username and password. Please create one under Integration
            tools.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>MyCarrierPortal API User's username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your MyCarrierPortal API User username" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>MyCarrierPortal API User's password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Enter your MyCarrierPortal API User password"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="onboardingURL"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>MyCarrierPortal Intellivite Link</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Intellivite Link can be found under 'Integration Tools'"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/my-carrier-portal"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
